import React, { useEffect, useRef, useState } from 'react'
import flvjs from 'flv.js'
import useProgressWatch, { sampleOf } from '../lib/useProgressWatch'
import { emit, playQuietly, playerCallbackShape } from './playerProps'

export default function FlvPlayer({
  url,
  muted,
  onConnecting,
  onPlaying,
  onStall,
  onResume,
  onError,
  onSpeed,
  onAutoMuted,
  onBlocked,
}) {
  const videoRef = useRef(null)
  const playerRef = useRef(null)
  const nameRef = useRef('flv.js')
  const [ready, setReady] = useState(false)

  const cb = useRef({})
  cb.current = {
    onConnecting,
    onPlaying,
    onStall,
    onResume,
    onError,
    onSpeed,
    onAutoMuted,
    onBlocked,
  }

  const snapshot = () => ({
    ...sampleOf(videoRef.current),
    player: nameRef.current,
  })

  useEffect(() => {
    const fail = (code, detail) => emit(cb, 'onError', code, detail, snapshot())
    emit(cb, 'onConnecting')

    const start = () => {
      // flv.js ต้องใช้ MSE ซึ่ง Safari บน iPhone ไม่มี — แจ้ง unsupported แทนการค้างเงียบ
      if (!flvjs.isSupported()) {
        fail('unsupported', 'เบราว์เซอร์นี้เล่น FLV ไม่ได้ (ไม่รองรับ MSE)')
        return
      }
      nameRef.current = `flv.js ${flvjs.version}`

      const player = flvjs.createPlayer(
        { type: 'flv', url, isLive: true, cors: true },
        { enableStashBuffer: false, stashInitialSize: 128 },
      )

      player.on(flvjs.Events.ERROR, (type, detail) =>
        fail(String(type), String(detail)),
      )

      player.on(flvjs.Events.STATISTICS_INFO, stats => {
        if (stats && typeof stats.speed === 'number') {
          emit(cb, 'onSpeed', stats.speed)
        }
      })

      player.attachMediaElement(videoRef.current)
      player.load()
      playerRef.current = player
      setReady(true)
      playQuietly(videoRef.current, cb)
    }

    try {
      start()
    } catch (err) {
      fail('OtherError', err && err.message)
    }

    return () => {
      setReady(false)
      const player = playerRef.current
      playerRef.current = null
      if (!player) return
      try {
        player.pause()
        player.unload()
        player.detachMediaElement()
        player.destroy()
      } catch (e) {
        /* player อาจถูกทำลายไปแล้ว */
      }
    }
    // ผูกกับ url อย่างเดียวตามตั้งใจ — callback / snapshot อ่านผ่าน ref เสมอ
  }, [url])

  useProgressWatch(
    videoRef,
    {
      onFirstFrame: () => emit(cb, 'onPlaying', snapshot()),
      onStall: () => emit(cb, 'onStall', snapshot()),
      onResume: () => emit(cb, 'onResume', snapshot()),
    },
    ready,
  )

  return <video ref={videoRef} muted={muted} playsInline autoPlay />
}

FlvPlayer.propTypes = playerCallbackShape
